// Valid subscription tier values
export const VALID_TIERS = ['free', 'base', 'pro', 'ultra'] as const;
export type ValidTier = typeof VALID_TIERS[number];

// Valid yearly value
export const VALID_YEARLY = 'true';

// Subscription tier display names
export const TIER_DISPLAY_NAMES: Record<ValidTier, string> = {
  free: 'Free',
  base: 'Base',
  pro: 'Pro',
  ultra: 'Ultra'
};

// Subscription tier badge classes for UI styling
export const TIER_BADGE_CLASSES: Record<ValidTier, string> = {
  free: 'badge-neutral',
  base: 'badge-info',
  pro: 'badge-primary',
  ultra: 'badge-secondary'
};

// Subscription tier descriptions
export const TIER_DESCRIPTIONS: Record<ValidTier, string> = {
  free: 'Basic features for getting started',
  base: 'Essential features for small teams',
  pro: 'Advanced features for growing businesses',
  ultra: 'Unlimited features for enterprise needs'
};

// Subscription tier limits (example - adjust based on your actual limits)
export const TIER_LIMITS: Record<ValidTier, {
  uploads: number;
  analyzes: number;
  storage: string;
  teamSeats: number;
  projects: number;
  apiCalls: number;
}> = {
  free: {
    uploads: 10,
    analyzes: 3,
    storage: '15 GB',
    teamSeats: 1,
    projects: 3,
    apiCalls: 0
  },
  base: {
    uploads: 1000,
    analyzes: 500,
    storage: '100 GB',
    teamSeats: 5,
    projects: 10,
    apiCalls: 0
  },
  pro: {
    uploads: 10000,
    analyzes: 5000,
    storage: '1000 GB',
    teamSeats: 20,
    projects: 50,
    apiCalls: 10000
  },
  ultra: {
    uploads: 999999, // Unlimited
    analyzes: 999999, // Unlimited
    storage: 'Unlimited',
    teamSeats: 100,
    projects: 999999, // Unlimited
    apiCalls: 999999 // Unlimited
  }
};

// Utility functions
export const getTierDisplayName = (tier: string): string => {
  const validTier = tier.toLowerCase() as ValidTier;
  return TIER_DISPLAY_NAMES[validTier] || tier;
};

export const getTierBadgeClass = (tier: string): string => {
  const validTier = tier.toLowerCase() as ValidTier;
  return TIER_BADGE_CLASSES[validTier] || 'badge-neutral';
};

export const getTierDescription = (tier: string): string => {
  const validTier = tier.toLowerCase() as ValidTier;
  return TIER_DESCRIPTIONS[validTier] || 'Unknown tier';
};

export const getTierLimits = (tier: string) => {
  const validTier = tier.toLowerCase() as ValidTier;
  return TIER_LIMITS[validTier] || TIER_LIMITS.free;
};

export const isValidTier = (tier: string): tier is ValidTier => {
  return VALID_TIERS.includes(tier.toLowerCase() as ValidTier);
};

export const isUnlimitedTier = (tier: string): boolean => {
  return tier.toLowerCase() === 'ultra';
};

// Feature definitions for each tier
export interface TierFeature {
  name: string;
  description: string;
  available: boolean;
  limit?: string | number;
  unlimited?: boolean;
}

export interface TierFeatures {
  [featureKey: string]: TierFeature;
}

// Feature keys - centralized list for type safety
export const FEATURE_KEYS = {
  IMAGE_UPLOAD: 'imageUpload',
  IMAGE_ANALYSIS: 'imageAnalysis',
  PROJECT_CREATION: 'projectCreation',
  TEAM_COLLABORATION: 'teamCollaboration',
  ADVANCED_ANALYTICS: 'advancedAnalytics',
  API_ACCESS: 'apiAccess',
  PRIORITY_SUPPORT: 'prioritySupport',
  CUSTOM_MODELS: 'customModels',
  DATA_EXPORT: 'dataExport',
  WHITE_LABEL: 'whiteLabel',
  REPORTING: 'reporting'
} as const;

export type FeatureKey = typeof FEATURE_KEYS[keyof typeof FEATURE_KEYS];

// Define all available features
export const ALL_FEATURES = {
  [FEATURE_KEYS.IMAGE_UPLOAD]: {
    name: 'Image Upload',
    description: 'Upload and store images for analysis',
    available: true
  },
  [FEATURE_KEYS.IMAGE_ANALYSIS]: {
    name: 'Image Analysis',
    description: 'Analyze images using AI models',
    available: true
  },
  [FEATURE_KEYS.PROJECT_CREATION]: {
    name: 'Project Creation',
    description: 'Create and organize projects',
    available: true
  },
  [FEATURE_KEYS.TEAM_COLLABORATION]: {
    name: 'Team Collaboration',
    description: 'Share projects with team members',
    available: false
  },
  [FEATURE_KEYS.ADVANCED_ANALYTICS]: {
    name: 'Advanced Analytics',
    description: 'Detailed analytics and reporting',
    available: false
  },
  [FEATURE_KEYS.API_ACCESS]: {
    name: 'API Access',
    description: 'Programmatic access to features',
    available: false
  },
  [FEATURE_KEYS.PRIORITY_SUPPORT]: {
    name: 'Priority Support',
    description: 'Priority customer support',
    available: false
  },
  [FEATURE_KEYS.CUSTOM_MODELS]: {
    name: 'Custom Models',
    description: 'Train and use custom AI models',
    available: false
  },
  [FEATURE_KEYS.DATA_EXPORT]: {
    name: 'Data Export',
    description: 'Export data in various formats',
    available: false
  },
  [FEATURE_KEYS.WHITE_LABEL]: {
    name: 'White Label',
    description: 'Remove branding and customize interface',
    available: false
  },
  [FEATURE_KEYS.REPORTING]: {
    name: 'Reporting',
    description: 'Generate and export reports',
    available: false
  }
} as const;

// Feature availability by tier
export const TIER_FEATURES: Record<ValidTier, TierFeatures> = {
  free: {
    [FEATURE_KEYS.IMAGE_UPLOAD]: {
      ...ALL_FEATURES[FEATURE_KEYS.IMAGE_UPLOAD],
      limit: `${getTierLimits('free').uploads} uploads/month`,
      unlimited: false
    },
    [FEATURE_KEYS.IMAGE_ANALYSIS]: {
      ...ALL_FEATURES[FEATURE_KEYS.IMAGE_ANALYSIS],
      limit: `${getTierLimits('free').analyzes} analyses/month`,
      unlimited: false
    },
    [FEATURE_KEYS.PROJECT_CREATION]: {
      ...ALL_FEATURES[FEATURE_KEYS.PROJECT_CREATION],
      limit: `${getTierLimits('free').projects} projects`,
      unlimited: false
    },
    [FEATURE_KEYS.TEAM_COLLABORATION]: {
      ...ALL_FEATURES[FEATURE_KEYS.TEAM_COLLABORATION],
      available: true,
      limit: `${getTierLimits('free').teamSeats} team members`,
      unlimited: false
    },
    [FEATURE_KEYS.ADVANCED_ANALYTICS]: {
      ...ALL_FEATURES[FEATURE_KEYS.ADVANCED_ANALYTICS],
      available: false
    },
    [FEATURE_KEYS.API_ACCESS]: {
      ...ALL_FEATURES[FEATURE_KEYS.API_ACCESS],
      available: false
    },
    [FEATURE_KEYS.PRIORITY_SUPPORT]: {
      ...ALL_FEATURES[FEATURE_KEYS.PRIORITY_SUPPORT],
      available: false
    },
    [FEATURE_KEYS.CUSTOM_MODELS]: {
      ...ALL_FEATURES[FEATURE_KEYS.CUSTOM_MODELS],
      available: false
    },
    [FEATURE_KEYS.DATA_EXPORT]: {
      ...ALL_FEATURES[FEATURE_KEYS.DATA_EXPORT],
      available: false
    },
    [FEATURE_KEYS.WHITE_LABEL]: {
      ...ALL_FEATURES[FEATURE_KEYS.WHITE_LABEL],
      available: false
    },
    [FEATURE_KEYS.REPORTING]: {
      ...ALL_FEATURES[FEATURE_KEYS.REPORTING],
      available: false
    }
  },
  base: {
    imageUpload: {
      ...ALL_FEATURES.imageUpload,
      limit: `${getTierLimits('base').uploads} uploads/month`,
      unlimited: false
    },
    imageAnalysis: {
      ...ALL_FEATURES.imageAnalysis,
      limit: `${getTierLimits('base').analyzes} analyses/month`,
      unlimited: false
    },
    projectCreation: {
      ...ALL_FEATURES.projectCreation,
      limit: `${getTierLimits('base').projects} projects`,
      unlimited: false
    },
    teamCollaboration: {
      ...ALL_FEATURES.teamCollaboration,
      available: true,
      limit: `${getTierLimits('base').teamSeats} team members`,
      unlimited: false
    },
    advancedAnalytics: {
      ...ALL_FEATURES.advancedAnalytics,
      available: false
    },
    apiAccess: {
      ...ALL_FEATURES.apiAccess,
      available: false
    },
    prioritySupport: {
      ...ALL_FEATURES.prioritySupport,
      available: false
    },
    customModels: {
      ...ALL_FEATURES.customModels,
      available: false
    },
    dataExport: {
      ...ALL_FEATURES.dataExport,
      available: true,
      limit: 'CSV, JSON formats',
      unlimited: true
    },
    whiteLabel: {
      ...ALL_FEATURES.whiteLabel,
      available: false
    },
    reporting: {
      ...ALL_FEATURES.reporting,
      available: true,
      limit: 'Basic reports',
      unlimited: false
    }
  },
  pro: {
    imageUpload: {
      ...ALL_FEATURES.imageUpload,
      limit: `${getTierLimits('pro').uploads} uploads/month`,
      unlimited: false
    },
    imageAnalysis: {
      ...ALL_FEATURES.imageAnalysis,
      limit: `${getTierLimits('pro').analyzes} analyses/month`,
      unlimited: false
    },
    projectCreation: {
      ...ALL_FEATURES.projectCreation,
      limit: `${getTierLimits('pro').projects} projects`,
      unlimited: false
    },
    teamCollaboration: {
      ...ALL_FEATURES.teamCollaboration,
      available: true,
      limit: `${getTierLimits('pro').teamSeats} team members`,
      unlimited: false
    },
    advancedAnalytics: {
      ...ALL_FEATURES.advancedAnalytics,
      available: true,
      unlimited: true
    },
    apiAccess: {
      ...ALL_FEATURES.apiAccess,
      available: true,
      limit: `${getTierLimits('pro').apiCalls} API calls/month`,
      unlimited: false
    },
    prioritySupport: {
      ...ALL_FEATURES.prioritySupport,
      available: true,
      unlimited: true
    },
    customModels: {
      ...ALL_FEATURES.customModels,
      available: false
    },
    dataExport: {
      ...ALL_FEATURES.dataExport,
      available: true,
      limit: 'All formats',
      unlimited: true
    },
    whiteLabel: {
      ...ALL_FEATURES.whiteLabel,
      available: false
    },
    reporting: {
      ...ALL_FEATURES.reporting,
      available: true,
      limit: 'Advanced reports',
      unlimited: true
    }
  },
  ultra: {
    imageUpload: {
      ...ALL_FEATURES.imageUpload,
      limit: 'Unlimited',
      unlimited: true
    },
    imageAnalysis: {
      ...ALL_FEATURES.imageAnalysis,
      limit: 'Unlimited',
      unlimited: true
    },
    projectCreation: {
      ...ALL_FEATURES.projectCreation,
      limit: 'Unlimited',
      unlimited: true
    },
    teamCollaboration: {
      ...ALL_FEATURES.teamCollaboration,
      available: true,
      limit: 'Unlimited team members',
      unlimited: true
    },
    advancedAnalytics: {
      ...ALL_FEATURES.advancedAnalytics,
      available: true,
      unlimited: true
    },
    apiAccess: {
      ...ALL_FEATURES.apiAccess,
      available: true,
      limit: 'Unlimited API calls',
      unlimited: true
    },
    prioritySupport: {
      ...ALL_FEATURES.prioritySupport,
      available: true,
      unlimited: true
    },
    customModels: {
      ...ALL_FEATURES.customModels,
      available: true,
      unlimited: true
    },
    dataExport: {
      ...ALL_FEATURES.dataExport,
      available: true,
      limit: 'All formats',
      unlimited: true
    },
    whiteLabel: {
      ...ALL_FEATURES.whiteLabel,
      available: true,
      unlimited: true
    },
    reporting: {
      ...ALL_FEATURES.reporting,
      available: true,
      limit: 'Unlimited reports',
      unlimited: true
    }
  }
};

// Utility functions for features
export const getTierFeatures = (tier: string): TierFeatures => {
  const validTier = tier.toLowerCase() as ValidTier;
  return TIER_FEATURES[validTier] || TIER_FEATURES.free;
};

export const isFeatureAvailable = (tier: string, featureKey: FeatureKey): boolean => {
  const features = getTierFeatures(tier);
  return features[featureKey]?.available || false;
};

export const getFeatureLimit = (tier: string, featureKey: FeatureKey): string | number | undefined => {
  const features = getTierFeatures(tier);
  return features[featureKey]?.limit;
};

export const isFeatureUnlimited = (tier: string, featureKey: FeatureKey): boolean => {
  const features = getTierFeatures(tier);
  return features[featureKey]?.unlimited || false;
};

export const getAvailableFeatures = (tier: string): TierFeature[] => {
  const features = getTierFeatures(tier);
  return Object.values(features).filter(feature => feature.available);
};

export const getUnavailableFeatures = (tier: string): TierFeature[] => {
  const features = getTierFeatures(tier);
  return Object.values(features).filter(feature => !feature.available);
};

export const compareTierFeatures = (currentTier: string, newTier: string): {
  gained: TierFeature[];
  lost: TierFeature[];
  unchanged: TierFeature[];
} => {
  const currentFeatures = getTierFeatures(currentTier);
  const newFeatures = getTierFeatures(newTier);
  
  const gained: TierFeature[] = [];
  const lost: TierFeature[] = [];
  const unchanged: TierFeature[] = [];
  
  Object.keys(ALL_FEATURES).forEach(featureKey => {
    const currentAvailable = currentFeatures[featureKey]?.available || false;
    const newAvailable = newFeatures[featureKey]?.available || false;
    
    if (newAvailable && !currentAvailable) {
      gained.push(newFeatures[featureKey]);
    } else if (currentAvailable && !newAvailable) {
      lost.push(currentFeatures[featureKey]);
    } else if (currentAvailable && newAvailable) {
      unchanged.push(newFeatures[featureKey]);
    }
  });
  
  return { gained, lost, unchanged };
};
